import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientQuery, clientMutation } from "../../../../../../../core/data/GraphQLApi";
import { endpoints } from "../../../../../../../core/defaultValues";
import { ExceptionManager } from "../../../../../../../core/logManager";
import { resetALL } from "../../../../../../../.globals/redux/actions";

const INIT_STATE = {
    loading: false,
    open: false,
    create: null,
    update: null,
    workOrderStatus: null,
    workOrderStatusId: null,
    workOrderStatusList: [],
};

const route = "modules/settings/views/workOrderStatus/modals/createEditManageWorkOrderStatus/redux/slice.";

export const manageWorkOrderCreateWorkOrderStatus = createAsyncThunk("manage/createEditWorkOrderStatus/createWorkOrderStatus",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await clientMutation(
                `
                    mutation createWorkOrderStatus ($name: String!, $description: String, $color: String!, $category: enumWorkOrderStatusCategory!) {
                        result: createWorkOrderStatus (name: $name, description: $description, color: $color, category: $category) {
                            id
                            name
                            description
                            color
                        }
                    }
                `,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.result
        } catch (exc) {
            ExceptionManager(exc, route, "createWorkOrderStatus");
            return rejectWithValue(exc);
        }
    }
);

export const manageWorkOrderEditWorkOrderStatus = createAsyncThunk("manage/createEditWorkOrderStatus/updateWorkOrderStatus",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await clientMutation(
                `
                    mutation updateWorkOrderStatus ($id: Int!, $name: String, $description: String, $color: String, $category: enumWorkOrderStatusCategory) {
                        result: updateWorkOrderStatus (id: $id, name: $name, description: $description, color: $color, category: $category) {
                            id
                            name
                            description
                            color
                        }
                    }
                `,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.result
        } catch (exc) {
            ExceptionManager(exc, route, "updateWorkOrderStatus");
            return rejectWithValue(exc);
        }
    }
);

export const manageWorkOrderGetWorkOrderStatus = createAsyncThunk("manage/createEditWorkOrderStatus/getWorkOrderStatus",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await clientQuery(
                `
                    query getWorkOrderStatus ($id: Int!) {
                        result: getWorkOrderStatus (id: $id) {
                            id
                            name
                            description
                            color
                            category
                        }
                    }
                `,
                payload,
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.result || null;
        } catch (exc) {
            ExceptionManager(exc, route, "getWorkOrderStatus");
            return rejectWithValue(exc);
        }
    }
);

export const manageWorkOrderSearchWorkOrderStatus = createAsyncThunk("manage/createEditWorkOrderStatus/searchWorkOrderStatuses",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await clientQuery(
                `
                    query ($conditions: [WorkOrderStatusSearchInput], $sort: [WorkOrderStatusSortInput], $limit: Int, $offset: Int) {
                        data: searchWorkOrderStatuses(
                            conditions: $conditions
                            sort: $sort
                            limit: $limit
                            offset: $offset
                        ) {
                        total
                        items {
                                id
                                name
                                description
                                color
                                companyId
                            }
                        }
                    }
                `,
                {},
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.data?.items || [];
        } catch (exc) {
            ExceptionManager(exc, route, "searchWorkOrderStatuses");
            return rejectWithValue(exc);
        }
    }
);

//Slice
const manageCreateEditWorkOrderStatusRedux = createSlice({
    name: "manage/createEditWorkOrderStatus",
    initialState: INIT_STATE,
    reducers: {
        reset(state, action) {
            return action.payload ? { ...state, ...action.payload } : INIT_STATE;
        },
        openModalCreateEditWorkOrderStatus(state, action) {
            state.open = true;
            state.workOrderStatusId = action?.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE);

        //manageWorkOrderCreateWorkOrderStatus
        builder.addCase(manageWorkOrderCreateWorkOrderStatus.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(manageWorkOrderCreateWorkOrderStatus.fulfilled, (state, action) => {
            state.loading = false;
            state.create = action.payload;
        });
        builder.addCase(manageWorkOrderCreateWorkOrderStatus.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        //manageWorkOrderEditWorkOrderStatus
        builder.addCase(manageWorkOrderEditWorkOrderStatus.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(manageWorkOrderEditWorkOrderStatus.fulfilled, (state, action) => {
            state.loading = false;
            state.update = action.payload;
        });
        builder.addCase(manageWorkOrderEditWorkOrderStatus.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        //manageWorkOrderGetWorkOrderStatus
        builder.addCase(manageWorkOrderGetWorkOrderStatus.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(manageWorkOrderGetWorkOrderStatus.fulfilled, (state, action) => {
            state.loading = false;
            state.workOrderStatus = action.payload;
        });
        builder.addCase(manageWorkOrderGetWorkOrderStatus.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        //manageWorkOrderSearchWorkOrderStatus
        builder.addCase(manageWorkOrderSearchWorkOrderStatus.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(manageWorkOrderSearchWorkOrderStatus.fulfilled, (state, action) => {
            state.loading = false;
            state.workOrderStatusList = action.payload;
        });
        builder.addCase(manageWorkOrderSearchWorkOrderStatus.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = manageCreateEditWorkOrderStatusRedux;

// Extract and export each action creator by name
export const { reset, openModalCreateEditWorkOrderStatus } = actions;

// Export the reducer, either as a default or named export
export default reducer;